import { ehLancamentoCancelado } from '../utils/duplicata';
import { useExemplos } from '../utils/exemplosPessoais';
import React, { useState, useRef, useEffect } from 'react';
import {
  parseQuickEntry, saveEntryRule, createCategory, createPaymentMethod,
  QuickEntry as QuickEntryData, EntryGap, ParsedInvestment,
} from '../services/api';
import { Category } from '../types/Category';
import { PaymentMethod } from '../types/PaymentMethod';
import { TransactionType, PaymentType } from '../types/Transaction';
import InvestmentProposal from './InvestmentProposal';
import Toast from './Toast';
import './QuickEntry.css';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import { redecidirDatas } from '../utils/dataDoCartao';
import { formatBRL } from '../utils/format';
import { useTelaEstreita } from '../utils/telaEstreita';
import { ehTetoDeIa, AVISO_TETO_DE_IA } from '../utils/tetoDeIa';

interface QuickEntryProps {
  categories: Category[];
  paymentMethods: PaymentMethod[];
  onCreate: (data: any) => Promise<void>;
  /**
   * Abre o formulário completo, levando junto o que já foi interpretado —
   * clicar em "preencher manualmente" e perder o trabalho seria hostil.
   * `prefill` vem null quando ela abre o formulário sem ter interpretado nada.
   */
  onOpenForm: (prefill: QuickEntryPrefill | null) => void;
  /** Fecha o formulário manual. Usado ao alternar e ao voltar a digitar. */
  onCloseForm: () => void;
  /** Se o formulário manual está aberto agora — o botão vira "fechar". */
  formAberto: boolean;
  /** Avisa o pai para recarregar, quando algo é criado aqui na hora. */
  onCategoryCreated?: () => void;
  onPaymentMethodCreated?: () => void;
  /**
   * "investi 5 mil em CDB no Nubank que rende 6% ao ano" não é um gasto — é uma
   * aplicação, e vai para a tabela de investimentos.
   *
   * Chega por aqui e não por um segundo campo porque o campo único é UM só:
   * obrigá-la a saber de antemão se o que vai escrever é "gasto" ou
   * "investimento" recria o formulário que ele veio eliminar.
   */
  onCreateInvestment?: (inv: ParsedInvestment) => Promise<void>;
  /**
   * Ela divide uma casa com alguém. Liga o toque "Meu / Da casa" na prévia —
   * sem isso, marcar um gasto como da casa exigia abrir o formulário inteiro.
   */
  temCasa?: boolean;
  /**
   * Sobe a cada gravação feita pelo formulário manual.
   *
   * Quando ela abre o formulário a partir de uma frase interpretada, os dois
   * ficam na tela: a revisão em cima, o formulário embaixo. Salvando pelo
   * formulário, a revisão descrevia algo que já virou lançamento — e continuar
   * mostrando "Adicionar" ao lado dela convida a lançar de novo.
   */
  salvoNoFormulario?: number;
  /**
   * Vários lançamentos lidos de UMA foto (o Scan e o colar do formulário).
   * `vez` muda a cada leitura: é ela que dispara a fila, e não a lista, que
   * pode vir igual duas vezes.
   */
  leitura?: { entries: QuickEntryData[]; vez: number } | null;
}

/** O que o formulário precisa para nascer preenchido. */
export interface QuickEntryPrefill {
  description: string;
  value: number | null;
  date: string | null;
  /** A compra que o campo único decidiu, para o formulário não perdê-la. */
  purchaseDate?: string | null;
  type: 'expense' | 'income';
  categoryId: string | null;
  paymentMethodId: string | null;
  installments: number | null;
  currentInstallment: number | null;
}

/**
 * O campo único — a forma principal de lançar.
 *
 * Desenho, em três decisões:
 *
 * 1. **Escreve como fala.** "terça gastei 50 em uber no crédito" vira um
 *    lançamento. O formulário de 8 campos continua existindo, recolhido, para
 *    o que a interpretação não cobrir.
 * 2. **O que falta vem do histórico, não de um chute.** O modelo extrai só o
 *    que está escrito; o método de pagamento ausente é buscado nas transações
 *    anteriores daquela descrição, e mostrado COM a evidência ("15×, sempre").
 * 3. **Confirmar é um toque, e pode virar regra.** A caixa "não perguntar mais"
 *    só aparece quando o histórico é unânime — 1 ocorrência é coincidência,
 *    não padrão. É o que faz o atrito diminuir com o uso.
 */
const QuickEntry: React.FC<QuickEntryProps> = ({
  categories, paymentMethods, onCreate, onOpenForm, onCloseForm, formAberto,
  onCategoryCreated, onPaymentMethodCreated, onCreateInvestment, salvoNoFormulario,
  temCasa = false, leitura = null,
}) => {
  const estreita = useTelaEstreita();
  const [texto, setTexto] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Os chips falam com as palavras dela quando há dado — `utils/exemplosPessoais.ts`.
  const exemplos = useExemplos().lancamento;
  const [proposta, setProposta] = useState<QuickEntryData | null>(null);
  /*
   * A escolha EXPLÍCITA de "meu / da casa" para esta proposta. Vazio = ela não
   * tocou, e vale o padrão do método de pagamento — a mesma precedência do
   * formulário e de `escopoDoLancamento` no servidor.
   */
  const [escopo, setEscopo] = useState<'' | 'casa' | 'pessoal'>('');
  /** Aplicação interpretada — o outro desfecho possível do mesmo campo. */
  const [inv, setInv] = useState<ParsedInvestment | null>(null);
  const [basePeriodo, setBasePeriodo] = useState<'monthly' | 'annual'>('annual');
  const [textoOriginal, setTextoOriginal] = useState('');
  const [lembrar, setLembrar] = useState<Record<string, boolean>>({});
  const [salvando, setSalvando] = useState(false);
  /**
   * Item: criar na hora. Se a categoria ou o método que ela quer não existe,
   * mandar ela sair da tela para cadastrar e voltar é justamente o atrito que
   * este campo veio eliminar.
   */
  const [criandoEm, setCriandoEm] = useState<EntryGap['field'] | null>(null);
  const [nomeNovo, setNomeNovo] = useState('');
  const [criando, setCriando] = useState(false);
  /** Criados nesta sessão, para aparecerem nas opções sem recarregar a página. */
  const [extras, setExtras] = useState<Record<string, { id: string; name: string }[]>>({
    category: [], payment_method: [],
  });
  const [feito, setFeito] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /**
   * A fila: os lançamentos que vieram JUNTOS e ainda não foram conferidos.
   *
   * Uma foto de fatura (ou um texto com várias linhas) devolve vários
   * lançamentos, e o campo mostrava só o primeiro: os outros eram jogados
   * fora sem aviso (07/10). Agora eles esperam aqui, e cada um passa pela
   * mesma conferência de sempre, um de cada vez, com o contador dizendo
   * quantos faltam. Nada é gravado sem ela ver.
   */
  const [fila, setFila] = useState<QuickEntryData[]>([]);
  const [totalDaLeitura, setTotalDaLeitura] = useState(0);
  /**
   * "Usar este método em todas?" (08/10). A foto de um cartão não diz qual
   * cartão é: todas as compras chegam sem método, e escolher o mesmo em cada
   * uma era o trabalho que a foto veio tirar. A pergunta aparece quando ela
   * escolhe o método de uma e ainda há outras sem; um "não" cala a pergunta
   * até a próxima leitura.
   */
  const [mesmoMetodo, setMesmoMetodo] = useState<{ id: string; nome: string | null } | null>(null);
  const [recusouOMesmoMetodo, setRecusouOMesmoMetodo] = useState(false);

  /** Põe um lançamento na conferência. A regra "não perguntar mais" é da LINHA quando vieram vários. */
  const mostrar = (entrada: QuickEntryData, original: string) => {
    setTextoOriginal(original);
    setEscopo('');
    setProposta(entrada);
    setLembrar({});
    setMesmoMetodo(null);
  };

  /** Passa para o próximo da fila, ou fecha a conferência se acabou. */
  const proxima = () => {
    if (fila.length === 0) { setProposta(null); setTotalDaLeitura(0); return; }
    const [seguinte, ...resto] = fila;
    setFila(resto);
    mostrar(seguinte, seguinte.description);
  };

  const largarFila = () => { setFila([]); setTotalDaLeitura(0); setMesmoMetodo(null); setRecusouOMesmoMetodo(false); };

  // A foto lida pelo formulário chega aqui: o primeiro vai para a conferência,
  // os outros esperam na fila.
  const vezDaLeitura = leitura?.vez ?? 0;
  useEffect(() => {
    if (!leitura || leitura.entries.length === 0) return;
    setErro(null);
    setFeito(null);
    setInv(null);
    setFila(leitura.entries.slice(1));
    setTotalDaLeitura(leitura.entries.length);
    setRecusouOMesmoMetodo(false);
    mostrar(leitura.entries[0], leitura.entries[0].description);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vezDaLeitura]);

  /* Confirmação do que ela acabou de escrever: não obedece ao olhinho.
     Ver a nota longa em `DebtProposal.tsx`. */
  const fmt = (v: number) => formatBRL(v);

  // O formulário gravou: a revisão sai junto com ele, e a confirmação vai para
  // o toast — fechar tudo de uma vez sem dizer nada viraria "será que salvou?".
  //
  // Compara com o valor visto NESTA montagem, não com zero: durante a edição
  // de uma transação existente o `QuickEntry` é desmontado, e o contador vive
  // no pai. Ao remontar, comparar com zero dispararia um "✓ Lançado" falso por
  // uma gravação que aconteceu enquanto ele nem estava na tela.
  const ultimoSalvo = useRef(salvoNoFormulario ?? 0);
  useEffect(() => {
    const atual = salvoNoFormulario ?? 0;
    if (atual === ultimoSalvo.current) return;
    ultimoSalvo.current = atual;
    setFeito(proposta ? `${proposta.description} · ${fmt(proposta.value)}` : 'lançamento salvo');
    // Ajustou um da fila no formulário: os outros continuam esperando.
    if (fila.length > 0) { proxima(); setInv(null); return; }
    setProposta(null);
    setTotalDaLeitura(0);
    setInv(null);
    setTexto('');
    setErro(null);
    setLembrar({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salvoNoFormulario]);

  const dataCurta = (iso: string) => {
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
  };

  /** O cartão moveu o pagamento para longe da compra — então as duas aparecem. */
  const movido = !!proposta && proposta.origemDoPagamento === 'cartao'
    && !!proposta.dataCompra && proposta.dataCompra !== proposta.date;

  const interpretar = async () => {
    if (!texto.trim() || carregando) return;
    setCarregando(true);
    setErro(null);
    setFeito(null);
    setInv(null);
    largarFila();
    try {
      const r = await parseQuickEntry(texto.trim());
      if (r.investment) {
        setTextoOriginal(texto.trim());
        setInv(r.investment);
        setBasePeriodo('annual');
        return;
      }
      if (r.entries.length === 0) {
        setErro('Não consegui identificar um gasto. Tente incluir o valor.');
        return;
      }
      const varios = r.entries.length > 1;
      setFila(r.entries.slice(1));
      setTotalDaLeitura(r.entries.length);
      mostrar(r.entries[0], varios ? r.entries[0].description : texto.trim());
    } catch (e: any) {
      /*
       * Teto de IA: abre o formulário em vez de virar beco sem saída.
       *
       * `alternarFormulario` já leva o texto dela junto — é o mesmo caminho do
       * botão "Preencher manualmente — leva o que você já escreveu". Aqui isso
       * pesa mais que lá: ela não escolheu o formulário, ele apareceu, e chegar
       * nele com o campo vazio pareceria que o app perdeu o que ela escreveu.
       */
      if (ehTetoDeIa(e)) {
        if (!formAberto) alternarFormulario();
        setErro(AVISO_TETO_DE_IA);
        return;
      }
      setErro(e?.response?.data?.error ?? 'Não consegui interpretar agora.');
    } finally {
      setCarregando(false);
    }
  };

  /**
   * O lançamento com o método escolhido — e o pagamento redecidido.
   *
   * Escolher o método pode MUDAR o pagamento — nunca a compra.
   *
   * O backend já decide as datas quando o método veio da frase ou do
   * histórico. Quando ele era uma lacuna, o pagamento foi decidido sem o
   * cartão existir — e é aqui, no clique dela, que o cartão aparece.
   *
   * `redecidirDatas` parte da COMPRA. Partir de `prev.date`, que é o
   * pagamento, somaria um mês toda vez que ela trocasse de método.
   *
   * A regra é a mesma do backend, presa pela tabela em
   * `shared/dataDoCartao.casos.json`.
   *
   * Uma função só para o lançamento da tela e para os da fila: o "usar em
   * todas" não pode decidir datas de um jeito diferente do toque em um.
   */
  const comMetodo = (entrada: QuickEntryData, id: string, nome: string | null): QuickEntryData => {
    const metodo = paymentMethods.find(x => x.id === id);
    const d = redecidirDatas(
      { compra: entrada.dataCompra ?? entrada.date, origemDaCompra: entrada.origemDaCompra ?? 'hoje' },
      metodo?.billingDay ?? null,
      metodo?.closingDay ?? null,
    );
    return {
      ...entrada,
      paymentMethodId: id,
      paymentMethodName: nome,
      date: d.pagamento,
      dataCompra: d.compra,
      origemDaCompra: d.origemDaCompra,
      origemDoPagamento: d.origemDoPagamento,
      diaDoCartao: d.diaDoCartao,
    };
  };

  /** Quem na fila ainda espera um método: só despesa, e só a que veio sem. */
  const semMetodo = (e: QuickEntryData) => !e.paymentMethodId && e.type !== 'income';

  const escolher = (field: EntryGap['field'], id: string, nomeExplicito?: string) => {
    if (field === 'payment_method') {
      const nome = nomeExplicito ?? paymentMethods.find(x => x.id === id)?.name
        ?? extras.payment_method.find(x => x.id === id)?.name ?? null;
      // Ela acabou de dizer o método de uma compra que veio sem: se as outras
      // da fila também vieram sem, vale perguntar uma vez em vez de N.
      if (proposta && semMetodo(proposta) && !recusouOMesmoMetodo && fila.some(semMetodo)) {
        setMesmoMetodo({ id, nome });
      }
      setProposta(prev => (prev ? comMetodo(prev, id, nome) : prev));
      return;
    }
    setProposta(prev => {
      if (!prev) return prev;
      const nome = nomeExplicito ?? categories.find(x => x.id === id)?.name
        ?? extras.category.find(x => x.id === id)?.name ?? null;
      // Agora é escolha DELA: o selo deixa de dizer "sugerida".
      return { ...prev, categoryId: id, categoryName: nome, filledByRule: { ...prev.filledByRule, category: null } };
    });
  };

  /** "Usar em todas": o método dela vai para as compras da fila que vieram sem. */
  const usarEmTodas = () => {
    if (!mesmoMetodo) return;
    const { id, nome } = mesmoMetodo;
    setFila(f => f.map(e => (semMetodo(e) ? comMetodo(e, id, nome) : e)));
    setMesmoMetodo(null);
  };

  const faltando = (): EntryGap['field'][] => {
    if (!proposta) return [];
    const f: EntryGap['field'][] = [];
    if (!proposta.categoryId) f.push('category');
    if (!proposta.paymentMethodId && proposta.type !== 'income') f.push('payment_method');
    return f;
  };

  const gravar = async () => {
    if (!proposta || salvando) return;
    setSalvando(true);
    try {
      await onCreate({
        type: proposta.type === 'income' ? TransactionType.INCOME : TransactionType.EXPENSE,
        paymentType: PaymentType.VARIABLE,
        description: proposta.description,
        value: proposta.value,
        hasInstallments: !!proposta.installments,
        installments: proposta.installments ?? undefined,
        // Não é sempre 1. "parcela 3/6" já tem duas pagas, e gravar como 1
        // faria a projeção cobrar dois meses a mais do que ainda falta.
        currentInstallment: proposta.installments
          ? (proposta.currentInstallment ?? 1)
          : undefined,
        isMonthly: false,
        paymentMethod: proposta.paymentMethodId,
        date: proposta.date,
        purchaseDate: proposta.dataCompra ?? null,
        categoryId: proposta.categoryId,
        // Só quando ela ESCOLHEU: sem isto o servidor herda do método.
        ...(escopo ? { escopo } : {}),
      });

      // Grava as regras que ela marcou. Falha aqui não desfaz o lançamento —
      // a regra é conveniência, o lançamento é o que importa.
      for (const [field, marcado] of Object.entries(lembrar)) {
        if (!marcado) continue;
        const value = field === 'category' ? proposta.categoryId : proposta.paymentMethodId;
        if (!value) continue;
        try {
          await saveEntryRule({
            matchType: 'description',
            matchValue: textoOriginal,
            field: field as EntryGap['field'],
            value,
          });
        } catch { /* silencioso de propósito */ }
      }

      setFeito(`${proposta.description} · ${fmt(proposta.value)}`);
      setTexto('');
      if (fila.length > 0) {
        proxima();
      } else {
        setProposta(null);
        setTotalDaLeitura(0);
        inputRef.current?.focus();
      }
    } catch (e) {
      // Ela viu o aviso de duplicata e desistiu: a proposta fica na tela, sem
      // mensagem de erro — nada falhou.
      if (!ehLancamentoCancelado(e)) setErro('Não consegui salvar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const gravarInvestimento = async () => {
    if (!inv || salvando || !onCreateInvestment) return;
    setSalvando(true);
    try {
      await onCreateInvestment(inv);
      setFeito(
        inv.operation === 'contribute'
          ? `aporte de ${fmt(inv.amount!)} em ${inv.name}`
          : `${inv.name} · ${fmt(inv.amount!)}`,
      );
      setInv(null);
      setTexto('');
      inputRef.current?.focus();
    } catch {
      setErro('Não consegui salvar o investimento. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Monta o prefill. Com proposta, leva tudo que foi interpretado — inclusive
   * os campos que ela já escolheu nas opções. Sem proposta, leva ao menos o
   * texto cru como descrição, para ela não redigitar.
   */
  /**
   * Cria a categoria ou o método na hora e já seleciona.
   *
   * Se o que ela quer não existe, mandar sair da tela para cadastrar e voltar
   * é justamente o atrito que este campo veio eliminar.
   */
  const criarNaHora = async (field: EntryGap['field']) => {
    const nome = nomeNovo.trim();
    if (!nome || criando) return;
    setCriando(true);
    try {
      if (field === 'category') {
        // Cor sorteada da paleta do app — ela ajusta depois se quiser.
        const cores = ['#6d28d9', '#db2777', '#dc2626', '#ea580c', '#059669', '#2563eb'];
        const c = await createCategory({ name: nome, color: cores[Math.floor(Math.random() * cores.length)] });
        setExtras(p => ({ ...p, category: [...p.category, { id: c.id, name: c.name }] }));
        escolher('category', c.id, c.name);
        onCategoryCreated?.();
      } else {
        const m = await createPaymentMethod({ name: nome });
        setExtras(p => ({ ...p, payment_method: [...p.payment_method, { id: m.id, name: m.name }] }));
        escolher('payment_method', m.id, m.name);
        onPaymentMethodCreated?.();
      }
      setCriandoEm(null);
      setNomeNovo('');
    } catch {
      setErro(`Não consegui criar "${nome}".`);
    } finally {
      setCriando(false);
    }
  };

  const alternarFormulario = () => {
    if (formAberto) { onCloseForm(); return; }
    if (proposta) {
      onOpenForm({
        description: proposta.description,
        value: proposta.value,
        date: proposta.date,
        purchaseDate: proposta.dataCompra ?? null,
        type: proposta.type,
        categoryId: proposta.categoryId,
        paymentMethodId: proposta.paymentMethodId,
        installments: proposta.installments,
        currentInstallment: proposta.currentInstallment,
      });
      return;
    }
    if (texto.trim()) {
      onOpenForm({
        description: texto.trim().slice(0, 60),
        value: null, date: null, type: 'expense',
        categoryId: null, paymentMethodId: null, installments: null,
        currentInstallment: null,
      });
      return;
    }
    onOpenForm(null);
  };

  const gaps = faltando();
  const pronto = proposta && gaps.length === 0;

  return (
    <div className="qe" id="qe-campo">
      <Toast message={feito && `Lançado: ${feito}`} onDismiss={() => setFeito(null)} />

      <div className="qe-input-row">
        <textarea
        maxLength={LIMITES.textoIa}
          ref={inputRef}
          className="qe-input"
          rows={1}
          placeholder={
            /* Medido num Android de 360px: o longo pede 217px numa caixa de
               216 — cortava por UM pixel. O curto guarda a gramática que
               ensina ("gastei X em Y"), e o exemplo completo continua no
               primeiro chip abaixo. Ver `utils/telaEstreita.ts`. */
            estreita ? 'gastei 50 em uber' : 'gastei 50 em uber no crédito'
          }
          value={texto}
          onChange={e => {
            setTexto(e.target.value);
            setProposta(null);
            largarFila();
            setInv(null);
            setFeito(null);
            // Voltar a digitar no campo único significa que ela desistiu do
            // formulário: manter os dois abertos deixa a tela confusa e sem
            // dizer qual dos dois vale.
            if (formAberto) onCloseForm();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); interpretar(); }
          }}
          disabled={carregando || salvando}
        />
        <button
          className="qe-go"
          onClick={interpretar}
          disabled={!texto.trim() || carregando || salvando}
          title="Interpretar (Enter)"
        >
          {carregando ? <span className="qe-spin" /> : '→'}
        </button>
      </div>

      {!proposta && !inv && !erro && (
        <div className="qe-hints">
          {exemplos.map(ex => (
            <button key={ex} className="qe-hint" onClick={() => { setTexto(ex); inputRef.current?.focus(); }}>
              {ex}
            </button>
          ))}
        </div>
      )}

      {erro && <div className="qe-erro">{erro}</div>}

      {!proposta && !inv && (
        <button type="button" className="qe-manual-fora" onClick={alternarFormulario}>
          {formAberto ? 'Fechar formulário' : 'Preencher manualmente'}
          {!formAberto && texto.trim() && <em> (leva o que você já escreveu)</em>}
        </button>
      )}

      {inv && (
        <InvestmentProposal
          inv={inv}
          onChange={setInv}
          onSave={gravarInvestimento}
          onCancel={() => setInv(null)}
          salvando={salvando}
          basePeriodo={basePeriodo}
          onBasePeriodo={setBasePeriodo}
        />
      )}

      {proposta && (
        <div className="qe-card">
          {totalDaLeitura > 1 && (
            <p className="qe-fila" role="status">
              {totalDaLeitura - fila.length} de {totalDaLeitura} lidos.
              {fila.length > 0 && (
                <button type="button" className="qe-fila-largar" onClick={() => { largarFila(); setProposta(null); }} disabled={salvando}>
                  descartar os {fila.length + 1} que faltam
                </button>
              )}
            </p>
          )}
          <div className="qe-linha">
            <span className="qe-desc">{proposta.description}</span>
            <span className={`qe-valor ${proposta.type === 'income' ? 'income' : ''}`}>
              {proposta.type === 'income' ? '+' : ''}{fmt(proposta.value)}
              {proposta.installments
                ? <em> ·  {proposta.currentInstallment
                    ? `${proposta.currentInstallment}/${proposta.installments}`
                    : `${proposta.installments}×`}</em>
                : null}
              {/*
                "4200 em 10x" vira dez de 420 — e ela precisa VER a divisão
                (05/10). O valor ao lado é o da parcela; sem o total escrito
                aqui, "R$ 420,00 · 10×" e "R$ 4.200,00 · 10×" são dois enganos
                opostos que a prévia mostraria do mesmo jeito.

                Só aparece enquanto a conta fecha: se ela mexer no valor ou nas
                parcelas, o total que escreveu deixa de valer.
              */}
              {proposta.totalInformado && proposta.installments && proposta.value !== null
                && Math.abs(proposta.value * proposta.installments - proposta.totalInformado) < proposta.installments * 0.01 + 0.001
                ? <small className="qe-total"> ({fmt(proposta.totalInformado)} no total)</small>
                : null}
            </span>
          </div>

          {/*
            Meu ou da casa, num toque (05/10).

            Só para quem divide uma casa. Fica FORA da fileira de etiquetas de
            propósito: aquelas dizem o que o app entendeu; esta é uma decisão
            dela, e precisa parecer botão. O estado mostrado é o que será
            gravado — a escolha, ou o padrão do método quando ela não tocou.
          */}
          {temCasa && (() => {
            const doMetodo = paymentMethods.find(m => m.id === proposta.paymentMethodId)?.escopoPadrao;
            const efetivo: 'casa' | 'pessoal' = escopo || doMetodo || 'pessoal';
            return (
              <div className="qe-escopo" role="group" aria-label="Este lançamento é meu ou da casa">
                {([['pessoal', 'Meu'], ['casa', 'Da casa']] as const).map(([valor, rotulo]) => (
                  <button
                    key={valor}
                    type="button"
                    className={`qe-escopo-op${efetivo === valor ? ' ativo' : ''}`}
                    aria-pressed={efetivo === valor}
                    onClick={() => setEscopo(valor)}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
            );
          })()}

          <div className="qe-chips">
            {/*
              Quando o cartão MOVEU o pagamento, o chip diz — e desde 13/09
              mostra a compra junto. Data trocada em silêncio decide em que mês
              o gasto entra, e o engano não aparece no mês: aparece nos que
              ainda não chegaram. A regra só é aceitável porque é visível.
            */}
            {movido && proposta.dataCompra && (
              <span className="qe-chip qe-chip-fixo" title="Quando você comprou. Não muda o mês do gasto.">
                {dataCurta(proposta.dataCompra)}<em> · compra</em>
              </span>
            )}
            <span
              className={`qe-chip qe-chip-fixo${movido ? ' qe-chip-assumido' : ''}`}
              title={movido
                ? `Assumi o dia ${proposta.diaDoCartao}, o do pagamento de ${proposta.paymentMethodName ?? 'do método'}. Abra o formulário para trocar.`
                : undefined}
            >
              {dataCurta(proposta.date)}
              {movido && <em> · pagamento</em>}
            </span>

            {proposta.categoryId ? (() => {
              /*
               * Na foto de uma lista a categoria já vem preenchida como
               * SUGESTÃO (do histórico dela ou da leitura), para ela não ter
               * de escolher em cada linha. Sugestão que não dá para trocar
               * seria chute gravado: o selo é um botão, e tocar nele reabre a
               * pergunta com as opções.
               */
              const origem = proposta.filledByRule.category;
              const sugerida = origem === 'historico' || origem === 'ia';
              const daParaTrocar = sugerida && proposta.gaps.some(g => g.field === 'category');
              if (daParaTrocar) {
                return (
                  <button
                    type="button"
                    className="qe-chip qe-chip-regra qe-chip-trocar"
                    onClick={() => setProposta(p => (p ? { ...p, categoryId: null, categoryName: null } : p))}
                    disabled={salvando}
                    title={origem === 'historico'
                      ? 'Sugerida pelo que você já lançou. Toque para trocar.'
                      : 'Sugerida pela leitura da foto. Toque para trocar.'}
                  >
                    {proposta.categoryName}<em> sugerida · trocar</em>
                  </button>
                );
              }
              return (
                <span className={`qe-chip${origem ? ' qe-chip-regra' : ''}`}>
                  {proposta.categoryName}
                  {origem === 'rule' && <em title="Preenchido por uma regra sua"> regra</em>}
                </span>
              );
            })() : null}

            {proposta.paymentMethodId ? (
              <span className={`qe-chip${proposta.filledByRule.paymentMethod ? ' qe-chip-regra' : ''}`}>
                {proposta.paymentMethodName}
                {proposta.filledByRule.paymentMethod && <em title="Preenchido por uma regra sua"> regra</em>}
              </span>
            ) : null}
          </div>

          {/* Uma pergunta por campo que faltou, já com o palpite do histórico. */}
          {proposta.gaps.filter(g => gaps.includes(g.field)).map(g => (
            <div className="qe-gap" key={g.field}>
              <div className="qe-gap-pergunta">
                {g.field === 'category' ? 'Qual categoria?' : 'Como você pagou?'}
                {g.options[0] && (
                  <span className="qe-gap-evidencia">
                    {g.options[0].matchedBy === 'global'
                      ? 'seu método mais usado'
                      : `${g.options[0].count}× ${g.options[0].unanimous ? 'sempre assim' : 'nesse caso'}`}
                  </span>
                )}
              </div>

              <div className="qe-opcoes">
                {g.options.map(o => (
                  <button
                    key={o.id}
                    className={`qe-opcao${o.id === g.suggestedId ? ' sugerida' : ''}`}
                    onClick={() => escolher(g.field, o.id)}
                  >
                    {o.name}
                  </button>
                ))}
                {criandoEm === g.field ? (
                  <div className="qe-criar">
                    <input
                      className="qe-criar-input"
                      autoFocus
                      placeholder={g.field === 'category' ? 'nome da categoria' : 'nome do método'}
                      value={nomeNovo}
                      onChange={e => setNomeNovo(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); criarNaHora(g.field); }
                        if (e.key === 'Escape') { setCriandoEm(null); setNomeNovo(''); }
                      }}
                      disabled={criando}
                    />
                    <button className="qe-criar-ok" onClick={() => criarNaHora(g.field)} disabled={!nomeNovo.trim() || criando} aria-busy={criando}>
                      criar
                    </button>
                    <button className="qe-criar-x" onClick={() => { setCriandoEm(null); setNomeNovo(''); }} disabled={criando}>✕</button>
                  </div>
                ) : (
                  <select
                    className="qe-outro"
                    value=""
                    onChange={e => {
                      if (e.target.value === '__novo__') {
                        // Se o que ela quer não existe, cadastrar aqui mesmo.
                        // Mandar sair da tela para criar e voltar é justamente
                        // o atrito que este campo veio eliminar.
                        setCriandoEm(g.field);
                        setNomeNovo('');
                      } else if (e.target.value) {
                        escolher(g.field, e.target.value);
                      }
                    }}
                  >
                    <option value="">outro…</option>
                    {/* União de tipos: normaliza para {id,name} antes de filtrar. */}
                    {(g.field === 'category'
                      ? [...categories.map(c => ({ id: c.id, name: c.name })), ...extras.category]
                      : [...paymentMethods.map(m => ({ id: m.id, name: m.name })), ...extras.payment_method]
                    )
                      .filter(x => !g.options.some(o => o.id === x.id))
                      .map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                    <option value="__novo__">
                      + criar {g.field === 'category' ? 'categoria' : 'método'}…
                    </option>
                  </select>
                )}
              </div>

              {/* Só quando o histórico é unânime: virar regra a partir de uma
                  coincidência é como o app começaria a gravar errado sozinho. */}
              {g.canRemember && (
                <label className="qe-lembrar">
                  <input
                    type="checkbox"
                    checked={!!lembrar[g.field]}
                    onChange={e => setLembrar({ ...lembrar, [g.field]: e.target.checked })}
                  />
                  não perguntar mais para “{g.rememberLabel}”
                </label>
              )}
            </div>
          ))}

          {mesmoMetodo && fila.some(semMetodo) && (() => {
            const quantas = fila.filter(semMetodo).length;
            return (
              <div className="qe-todas" role="group" aria-label="Usar o mesmo método nas outras">
                <span>
                  {quantas === 1 ? 'Outra compra também veio' : `Outras ${quantas} compras também vieram`} sem
                  método. Usar <strong>{mesmoMetodo.nome ?? 'este método'}</strong> {quantas === 1 ? 'nela' : 'em todas'}?
                </span>
                <span className="qe-todas-botoes">
                  <button type="button" className="qe-todas-sim" onClick={usarEmTodas} disabled={salvando}>
                    {quantas === 1 ? 'Usar' : 'Usar em todas'}
                  </button>
                  <button
                    type="button" className="qe-todas-nao" disabled={salvando}
                    onClick={() => { setMesmoMetodo(null); setRecusouOMesmoMetodo(true); }}
                  >
                    Não, escolho em cada uma
                  </button>
                </span>
              </div>
            );
          })()}

          <div className="qe-acoes">
            <button className="qe-salvar" onClick={gravar} disabled={!pronto || salvando} aria-busy={salvando}>
              {pronto ? 'Lançar' : 'Escolha as opções acima'}
            </button>
            <button className="qe-cancelar" onClick={() => (fila.length > 0 ? proxima() : (setProposta(null), setTotalDaLeitura(0)))} disabled={salvando}>
              {fila.length > 0 ? 'Pular este' : 'Cancelar'}
            </button>
            <button className="qe-manual" onClick={alternarFormulario} disabled={salvando}>
              {formAberto ? 'Fechar formulário' : 'Ajustar no formulário'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickEntry;
